import {
  AddButtonWrapper,
  ForenameColumn,
  GenderColumn,
  GuestCounter,
  GuestRow,
  InputsRow,
  RadioWrapper,
  RemoveGuestButtonWrapper,
  SurnameColumn,
  TitleColumn,
} from '@common/components/BookingForms/GuestField/GuestField.styles';
import RemoveGuestButton from '@common/components/BookingForms/GuestField/RemoveGuestButton';
import { Button } from '@common/components/Button';
import { BlockInputField } from '@common/components/FormElements';
import { BlockSelectField } from '@common/components/FormElements/BlockSelectField';
import { RadioGroupField } from '@common/components/RadioGroupField';
import { genderOptions, genderOptionsWithNeutral } from '@common/constants';
import { useTranslations } from '@lang';
import { AddIcon } from '@root/styles/icons';
import { femaleTitlesOptions, maleTitlesOptions } from '@utils/guestTitles';
import { Controller, UseFieldArrayReturn, useFormContext, useWatch } from 'react-hook-form';
import { SpaEntityFormValues } from '../SpaEntityForm.schema';

type SpaEntityGuestFieldProps = {
  fieldArray: UseFieldArrayReturn<SpaEntityFormValues, 'guests', 'id'>;
  minimumGuests: number | null;
  maximumGuests?: number | null;
  isGenderSpecific?: string | boolean;
  withGenderSelect?: boolean;
  showGenderNeutral?: boolean;
  defaultGender?: string;
};

export const SpaEntityGuestField = ({
  fieldArray,
  minimumGuests,
  maximumGuests,
  isGenderSpecific = false,
  withGenderSelect = true,
  showGenderNeutral = false,
  defaultGender,
}: SpaEntityGuestFieldProps) => {
  const { t } = useTranslations();
  const { control } = useFormContext<SpaEntityFormValues>();
  const { fields, append, remove } = fieldArray;
  const guestValues = useWatch({ control, name: 'guests' }) ?? [];

  const maleTitles = maleTitlesOptions.map((option) => ({
    ...option,
    label: t(option.label),
  }));

  const femaleTitles = femaleTitlesOptions.map((option) => ({
    ...option,
    label: t(option.label),
  }));

  const allTitlesOptions = [...maleTitles, ...femaleTitles];

  const getTitlesOptions = (genderValue?: string) => {
    if (!isGenderSpecific) return allTitlesOptions;
    if (genderValue === 'male') return maleTitles;
    if (genderValue === 'female') return femaleTitles;
    return allTitlesOptions;
  };

  const genderRadioOptions = showGenderNeutral ? genderOptionsWithNeutral : genderOptions;

  const handleRemove = (index: number) => {
    remove(index);
    if (fields.length - 1 < (minimumGuests ?? 0)) {
      append({ firstName: '', lastName: '', gender: defaultGender ?? '' });
    }
  };

  const handleAdd = () => {
    if (!maximumGuests || fields.length < maximumGuests) {
      append({ title: '', firstName: '', lastName: '', gender: defaultGender ?? '' });
    }
  };

  const canAddNextGuest = !maximumGuests || fields.length < maximumGuests;

  return (
    <>
      {fields.map((fieldEntry, index) => {
        const guestData = guestValues[index] ?? {};
        const isExistingBasketGuest = !!(guestData.externalSpaId || guestData.id);
        const titlesOptions = getTitlesOptions(guestData.gender ?? undefined);

        return (
          <GuestRow key={fieldEntry.id}>
            <GuestCounter>{t('spa.guestCounter', { counter: index + 1 })}</GuestCounter>
            <InputsRow>
              <TitleColumn>
                <Controller
                  control={control}
                  name={`guests.${index}.title`}
                  render={({ field: { onChange, value } }) => (
                    <BlockSelectField
                      useNativeDropdownOnMobile={false}
                      labelWidth="50px"
                      options={titlesOptions}
                      value={titlesOptions.find((opt) => opt.value === value) ?? null}
                      label={t('common.title')}
                      isRequired
                      isDisabled={isExistingBasketGuest}
                      isSearchable={false}
                      id={`${index}-title`}
                      onChange={(selected: any) => {
                        const val = selected?.value ?? '';
                        onChange(val);
                      }}
                    />
                  )}
                />
              </TitleColumn>
              <ForenameColumn>
                <Controller
                  control={control}
                  name={`guests.${index}.firstName`}
                  render={({ field: { onChange, value } }) => (
                    <BlockInputField
                      isRequired
                      label={t('common.firstName')}
                      placeholder={t('common.firstName')}
                      disabled={isExistingBasketGuest}
                      id={`${index}-fname`}
                      value={value ?? ''}
                      onChange={onChange}
                    />
                  )}
                />
              </ForenameColumn>
              <SurnameColumn>
                <Controller
                  control={control}
                  name={`guests.${index}.lastName`}
                  render={({ field: { onChange, value } }) => (
                    <BlockInputField
                      isRequired
                      label={t('common.lastName')}
                      placeholder={t('common.lastName')}
                      disabled={isExistingBasketGuest}
                      id={`${index}-lname`}
                      value={value ?? ''}
                      onChange={onChange}
                    />
                  )}
                />
              </SurnameColumn>
              <RemoveGuestButtonWrapper>
                <RemoveGuestButton
                  isVisible={isExistingBasketGuest || index >= (minimumGuests ?? 0)}
                  onClick={() => handleRemove(index)}
                />
              </RemoveGuestButtonWrapper>
            </InputsRow>
            {!isGenderSpecific && withGenderSelect && (
              <RadioWrapper>
                <Controller
                  control={control}
                  name={`guests.${index}.gender`}
                  render={({ field: { onChange, value }, fieldState: { error, isTouched } }) => (
                    <RadioGroupField
                      name={`guests.${index}.gender`}
                      input={{ value: value ?? '', onChange }}
                      meta={{ error: error?.message ?? '', touched: isTouched }}
                      error={isTouched && !!error}
                      options={genderRadioOptions}
                    />
                  )}
                />
              </RadioWrapper>
            )}
            {isGenderSpecific && (
              <GenderColumn>
                <Controller
                  control={control}
                  name={`guests.${index}.gender`}
                  render={({ field: { onChange, value }, fieldState: { error, isTouched } }) => (
                    <RadioGroupField
                      name={`guests.${index}.gender`}
                      input={{ value: value ?? '', onChange }}
                      meta={{ error: error?.message ?? '', touched: isTouched }}
                      error={isTouched && !!error}
                      options={genderRadioOptions}
                    />
                  )}
                />
              </GenderColumn>
            )}
          </GuestRow>
        );
      })}
      <AddButtonWrapper>
        {canAddNextGuest && (
          <Button
            type="button"
            isSmall
            onClick={handleAdd}
            icon={<AddIcon width="16" height="16" />}
          >
            {t('common.addMoreGuests')}
          </Button>
        )}
      </AddButtonWrapper>
    </>
  );
};
